import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import UploadFrame from "../../../assets/images/UploadFrame.png";
import PinIcon from "../../../assets/images/Pin.png";
import { CloseIcon, DownIcon } from "../../../assets/icons";
import { Select } from "antd";
import "antd/dist/reset.css"; // Add this for Ant Design v5 styles
import Loader from "../../Loader/Loader";
import {
  Actions,
  Btn,
  NextButton,
  Divider,
  DividerRow,
  DropdownWrapper,
  DropZone,
  Header,
  Modal,
  Or,
  Overlay,
  SubHeader,
  SubLink,
  UploadedFile,
  UploadedFileClose,
  UploadedFileInfo,
  UploadedFileName,
  UploadIconWrapper,
  UploadLabel,
  UploadLink,
  UploadText,
  CloseButton,
  StyledSelect,
  ErrorText,
  StyledTemplateWrapper,
} from "./SelectTemplateStyled";
import {
  fetchTemplateCategories,
  fetchTemplatesByCategoryId,
  fetchPackages,
} from "../../../service/settings";
import { fetchLetterheadList } from "../../../service/matter";
import styled from "styled-components";

const TabBar = styled.div`
  display: flex;
  gap: 0;
  margin-bottom: 16px;
`;

const Tab = styled.button`
  flex: 1;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  border: 1px solid #d1d5db;
  background: ${(p) => (p.$active ? "#0C53BB" : "#fff")};
  color: ${(p) => (p.$active ? "#fff" : "#374151")};
  cursor: pointer;
  &:first-child {
    border-radius: 6px 0 0 6px;
  }
  &:last-child {
    border-radius: 0 6px 6px 0;
    border-left: none;
  }
  &:hover {
    opacity: 0.9;
  }
`;

const PackageList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
  max-height: 280px;
  overflow-y: auto;
`;

const PackageOption = styled.div`
  padding: 12px;
  border: 2px solid ${(p) => (p.$selected ? "#0C53BB" : "#e5e7eb")};
  border-radius: 8px;
  cursor: pointer;
  background: ${(p) => (p.$selected ? "#eff6ff" : "#fff")};
  &:hover {
    border-color: #93c5fd;
  }
`;

const PackageTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #111827;
`;

const PackageDesc = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
`;

const PackageTemplates = styled.div`
  font-size: 11px;
  color: #9ca3af;
  margin-top: 4px;
`;

export default function SelectTemplatePopup({
  onClose,
  onTemplateSelect,
  onNext,
  onPackageSelect,
  selectedTemplate,
  setSelectedTemplate,
  file,
  setFile,
  showUploadedFile,
  setShowUploadedFile,
  handleError,
  selectedCategory,
  setSelectedCategory,
  selectedLetterhead: selectedLetterheadProp,
  setSelectedLetterhead: setSelectedLetterheadProp,
}) {
  const [dragActive, setDragActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [templateMap, setTemplateMap] = useState({});
  const [templatesForSelected, setTemplatesForSelected] = useState([]);
  const [templateDropdownOpen, setTemplateDropdownOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [letterheadList, setLetterheadList] = useState([]);

  // Package state
  const [activeTab, setActiveTab] = useState("templates");
  const [packageList, setPackageList] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  
  // Use internal state as fallback if prop is not provided
  const [internalSelectedLetterhead, setInternalSelectedLetterhead] = useState(null);
  
  // Use prop if provided, otherwise use internal state
  const selectedLetterhead = selectedLetterheadProp !== undefined ? selectedLetterheadProp : internalSelectedLetterhead;
  const setSelectedLetterhead = setSelectedLetterheadProp || setInternalSelectedLetterhead;

  const ALL_TEMPLATES_CATEGORY = {
    value: "ALL_CATEGORIES",
    label: "All Templates",
  };
  const [showTemplateError, setShowTemplateError] = useState(false);

  const inputRef = useRef();

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const allowedTypes = new Set([
    "application/pdf",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // docx
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // xlsx
  ]);
  const allowedExtensions = new Set([".pdf", ".docx", ".xlsx"]);

  const isFileTypeAllowed = (file) => {
    if (allowedTypes.has(file.type)) return true;

    // fallback: check extension if type is empty (some browsers)
    const ext = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
    return allowedExtensions.has(ext);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) {
      const file = e.dataTransfer.files[0];
      if (!isFileTypeAllowed(file)) {
        handleError("Only PDF, DOCX, or XLSX files are allowed.");
        return;
      }
      setFile(file);
      setShowUploadedFile(true);
      setSelectedTemplate(null);
      setSelectedLetterhead(null);
      setLetterheadList([]);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files?.[0]) {
      const file = e.target.files[0];
      if (!isFileTypeAllowed(file)) {
        handleError("Only PDF,DOCX, or XLSX files are allowed.");
        return;
      }
      setFile(file);
      setShowUploadedFile(true);
      setSelectedTemplate(null);
      setSelectedCategory(null);
      setShowTemplateError(false);
      setSelectedLetterhead(null);
      setLetterheadList([]);
    }
  };

  // Reset file/template on close
  const handleClose = () => {
    setFile(null);
    setShowUploadedFile(false);
    setSelectedLetterhead(null);
    setLetterheadList([]);
    onClose();
  };

  const handleRemoveFile = () => {
    setShowUploadedFile(false);
    setFile(null);
    setSelectedTemplate(null);
    setSelectedLetterhead(null);
    setLetterheadList([]);
  };

  const onSubmitNext = () => {
    if (activeTab === "packages" && selectedPackage) {
      onPackageSelect?.(selectedPackage, selectedLetterhead);
      return;
    }
    if (selectedTemplate?.is_letterhead_enabled && !selectedLetterhead) {
      handleError?.("Please select a letterhead to continue.");
      return;
    }
    onNext?.(file, selectedLetterhead);
  };

  const handleCategoryChange = (value) => {
    setShowTemplateError(false);
    setSelectedCategory(value);
    handleRemoveFile();
  };

  const handleTemplateDropdown = (open) => {
    // Block dropdown if no templates or no category
    if (!selectedCategory || templatesForSelected?.length === 0) {
      setShowTemplateError(true);
      setTemplateDropdownOpen(false);
      return;
    }

    setTemplateDropdownOpen(open);
  };

  const stopDragEvents = (e) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const fetchLetterheads = async () => {
    setIsLoading(true);
    try {
      const response = await fetchLetterheadList();
      const apiResponse = response?.data;
      const letterheads = apiResponse?.data || [];
      setLetterheadList(letterheads);
      
      // Only preselect default letterhead if no letterhead is already selected from props
      if (!selectedLetterheadProp) {
        const defaultLetterhead = letterheads.find((lh) => lh.is_default === true);
        if (defaultLetterhead) {
          setSelectedLetterhead(defaultLetterhead);
        }
      } else {
        // If we have a selected letterhead from props, verify it exists in the fetched list
        const letterheadExists = letterheads.find(lh => lh.id === selectedLetterheadProp.id);
        if (!letterheadExists) {
          // If the letterhead from props doesn't exist in the list, fall back to default
          const defaultLetterhead = letterheads.find((lh) => lh.is_default === true);
          if (defaultLetterhead) {
            setSelectedLetterhead(defaultLetterhead);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching letterhead list:", error);
      handleError?.("Error fetching letterheads");
    } finally {
      setIsLoading(false);
    }
  };

  const formatLetterheadLabel = (letterhead) => {
    const firmName = letterhead.firm_name || "";
    const location = letterhead.firm_location || "";
    const locationPart = location ? `/${location}` : "";
    return `${firmName}${locationPart}`;
  };

  const handleLetterheadSelect = (value) => {
    const letterhead = letterheadList.find((lh) => lh.id === value);
    setSelectedLetterhead(letterhead);
  };

  useEffect(() => {
    const fetchCategories = async () => {
      setIsLoading(true);
      try {
        if (!selectedCategory) setSelectedCategory("ALL_CATEGORIES");
        const categoryList = await fetchTemplateCategories();
        const formatted = categoryList.data.data.map((cat) => ({
          value: cat.key,
          label: cat.label,
        }));
        setCategories([ALL_TEMPLATES_CATEGORY, ...formatted]);
      } catch (error) {
        console.error("Error fetching categories:", error);
        handleError("Error in fetching categories");
      } finally {
        setIsLoading(false);
      }
    };

    const loadPackages = async () => {
      try {
        const res = await fetchPackages();
        setPackageList((res?.data || []).filter((p) => p.is_active));
      } catch (err) {
        console.error("Error fetching packages:", err);
      }
    };

    fetchCategories();
    loadPackages();
  }, []);

  useEffect(() => {
    if (!selectedCategory) return;

    const cachedTemplates = templateMap[selectedCategory];
    if (cachedTemplates) {
      setTemplatesForSelected(cachedTemplates);
      return;
    }

    const fetchTemplates = async () => {
      setIsLoading(true);
      try {
        const response = await fetchTemplatesByCategoryId(selectedCategory);
        const fetched = response?.data || [];
        // update both
        setTemplateMap((prev) => ({ ...prev, [selectedCategory]: fetched }));
        setTemplatesForSelected(fetched);
      } catch (error) {
        console.error("Error fetching templates:", error);
        handleError("Error in fetching templates");
      } finally {
        setIsLoading(false);
      }
    };

    fetchTemplates();
  }, [selectedCategory]);

  // Restore letterhead selection when returning from Fill Form Fields modal
  useEffect(() => {
    if (selectedTemplate?.is_letterhead_enabled) {
      // If template requires letterhead, fetch list if not already loaded
      if (letterheadList.length === 0) {
        fetchLetterheads();
      } else if (selectedLetterheadProp) {
        // If list is already loaded and we have a selected letterhead prop, verify it exists in the list
        const letterheadExists = letterheadList.find(lh => lh.id === selectedLetterheadProp?.id);
        if (!letterheadExists && selectedLetterheadProp?.id) {
          // If letterhead not found in list, refetch to ensure we have the latest list
          fetchLetterheads();
        }
      }
    }
  }, [selectedTemplate, selectedLetterheadProp]);

  return (
    <Overlay
      onDragEnter={stopDragEvents}
      onDragOver={stopDragEvents}
      onDrop={stopDragEvents}
    >
      <Modal>
        {isLoading && <Loader />}
        <CloseButton onClick={handleClose}>
          <CloseIcon />
        </CloseButton>
        <Header>Select Document Template</Header>
        <SubHeader>Choose from available templates or packages</SubHeader>

        {packageList.length > 0 && (
          <TabBar>
            <Tab $active={activeTab === "templates"} onClick={() => { setActiveTab("templates"); setSelectedPackage(null); }}>
              Templates
            </Tab>
            <Tab $active={activeTab === "packages"} onClick={() => { setActiveTab("packages"); setSelectedTemplate(null); setFile(null); setShowUploadedFile(false); if (letterheadList.length === 0) fetchLetterheads(); }}>
              Packages ({packageList.length})
            </Tab>
          </TabBar>
        )}

        {activeTab === "packages" ? (
          <>
            <SubLink>Select a package to merge all templates at once</SubLink>
            <PackageList>
              {packageList.map((pkg) => (
                <PackageOption
                  key={pkg.id}
                  $selected={selectedPackage?.id === pkg.id}
                  onClick={() => setSelectedPackage(pkg)}
                >
                  <PackageTitle>{pkg.name}</PackageTitle>
                  {pkg.description && <PackageDesc>{pkg.description}</PackageDesc>}
                  <PackageTemplates>
                    {pkg.template_count} template{pkg.template_count !== 1 ? "s" : ""}
                    {pkg.items?.length > 0 && `: ${pkg.items.map((it) => it.template_name).join(", ")}`}
                  </PackageTemplates>
                </PackageOption>
              ))}
            </PackageList>

            {/* Letterhead for package */}
            {selectedPackage && (
              <div style={{ marginBottom: 16 }}>
                <UploadLabel>Letterhead (optional)</UploadLabel>
                <StyledSelect
                  placeholder="No letterhead"
                  allowClear
                  value={selectedLetterhead?.id}
                  onChange={handleLetterheadSelect}
                  showSearch
                  style={{ width: "49%" }}
                  filterOption={(input, option) => {
                    const lh = letterheadList.find((l) => l.id === option.value);
                    return lh ? formatLetterheadLabel(lh).toLowerCase().includes(input.toLowerCase()) : false;
                  }}
                  options={letterheadList.map((lh) => ({
                    label: formatLetterheadLabel(lh),
                    value: lh.id,
                  }))}
                  getPopupContainer={(trigger) => trigger.parentNode}
                  suffixIcon={<DownIcon className="custom-arrow" color="#5A6C87" />}
                />
              </div>
            )}
          </>
        ) : (
        <>
        <SubLink>Use one of the predefined template</SubLink>
        <DropdownWrapper>
          <StyledSelect
            placeholder="Select Category"
            onChange={handleCategoryChange}
            options={categories}
            value={categories.find((c) => c.value === selectedCategory)}
            getPopupContainer={(trigger) => trigger.parentNode}
            suffixIcon={<DownIcon className="custom-arrow" color="#5A6C87" />}
          />
          <StyledTemplateWrapper>
            <StyledSelect
              value={selectedTemplate?.name}
              onChange={(value) => {
                const selectedObj = templatesForSelected.find(
                  (tpl) => tpl.id === value
                );
                setFile(null);
                setShowUploadedFile(false);
                onTemplateSelect?.(selectedObj);
                
                // Fetch letterheads if template requires letterhead
                if (selectedObj?.is_letterhead_enabled) {
                  fetchLetterheads();
                } else {
                  setLetterheadList([]);
                  setSelectedLetterhead(null);
                }
              }}
              placeholder="Search/Select from Template List"
              size="large"
              showSearch
              onFocus={() => {
                if (!selectedCategory || templatesForSelected.length === 0) {
                  setShowTemplateError(true);
                }
              }}
              optionFilterProp="children"
              filterOption={(input, option) =>
                option?.children?.toLowerCase().includes(input.toLowerCase())
              }
              getPopupContainer={(triggerNode) => triggerNode.parentNode}
              status={showTemplateError ? "error" : ""}
              open={templateDropdownOpen}
              onOpenChange={handleTemplateDropdown}
              suffixIcon={<DownIcon className="custom-arrow" color="#5A6C87" />}
            >
              {templatesForSelected?.length > 0 &&
                templatesForSelected.map((tpl) => (
                  <Select.Option key={tpl.id} value={tpl.id}>
                    {tpl.name}
                  </Select.Option>
                ))}
            </StyledSelect>

            {showTemplateError && !selectedCategory && (
              <ErrorText type="danger">
                Please select a category to view available templates.
              </ErrorText>
            )}
            {selectedCategory &&
              templatesForSelected?.length === 0 &&
              isLoading === false && (
                <ErrorText type="danger">
                  There are no templates for this category
                </ErrorText>
              )}
          </StyledTemplateWrapper>
        </DropdownWrapper>
        <>
         {/* Add Letterhead section - only show when template is selected and is_letterhead_enabled is true */}
         {selectedTemplate && selectedTemplate.is_letterhead_enabled &&(
          <div style={{ marginBottom: "24px" }}>
            <UploadLabel>Add Letterhead</UploadLabel>
            <SubLink style={{ marginBottom: "8px" }}>
              This template requires a letterhead
            </SubLink>
            <StyledSelect
              placeholder="Choose a letterhead"
              value={selectedLetterhead?.id}
              onChange={handleLetterheadSelect}
              showSearch
              style={{ width: '49%' }}
              filterOption={(input, option) => {
                const letterhead = letterheadList.find(lh => lh.id === option.value);
                if (!letterhead) return false;
                const searchText = formatLetterheadLabel(letterhead).toLowerCase();
                return searchText.includes(input.toLowerCase());
              }}
              options={letterheadList.map((letterhead) => ({
                label: (
                  <span style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <span>{formatLetterheadLabel(letterhead)}</span>
                    {letterhead.is_default && (
                      <span style={{ color: '#5A6C87', marginLeft: 'auto' }}>(default)</span>
                    )}
                  </span>
                ),
                value: letterhead.id,
              }))}
              getPopupContainer={(trigger) => trigger.parentNode}
              suffixIcon={<DownIcon className="custom-arrow" color="#5A6C87" />}
            />
          </div>
        )}

        {selectedTemplate && selectedTemplate.is_letterhead_enabled === false &&
          (
          <SubLink style={{ marginBottom: "8px" }}>
              This template does not require a letterhead
            </SubLink>
        )
        } 
        </>

        <DividerRow>
          <Divider />
          <Or>or</Or>
          <Divider />
        </DividerRow>
        <UploadLabel>Upload from System</UploadLabel>
        {!showUploadedFile && (
          <DropZone
            isDragActive={dragActive}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => inputRef.current.click()}
          >
            <UploadIconWrapper>
              <img src={UploadFrame} alt="Upload" />
            </UploadIconWrapper>
            <UploadText>
              Drag a file template in pdf, xlsx, docx formats here or{" "}
              <UploadLink
                onClick={(e) => {
                  e.stopPropagation();
                  inputRef.current.click();
                }}
              >
                click to upload
              </UploadLink>
            </UploadText>
            <input
              ref={inputRef}
              type="file"
              accept=".pdf,.docx,.xlsx"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
          </DropZone>
        )}
        {showUploadedFile && file && (
          <UploadedFile>
            <UploadedFileInfo>
              <img src={PinIcon} alt="Attachment" width={24} height={24} />
              <UploadedFileName>{file.name}</UploadedFileName>
            </UploadedFileInfo>
            <UploadedFileClose
              aria-label="Remove file"
              onClick={handleRemoveFile}
            >
              <CloseIcon />
            </UploadedFileClose>
          </UploadedFile>
        )}
        </>
        )}
        
        <Actions>
          <Btn type="button" onClick={handleClose}>Cancel</Btn>
          <NextButton
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onSubmitNext();
            }}
            disabled={
              activeTab === "packages"
                ? !selectedPackage
                : (!selectedTemplate && !file) ||
                  (selectedTemplate?.is_letterhead_enabled && !selectedLetterhead)
            }
          >
            Next
          </NextButton>
        </Actions>
      </Modal>
    </Overlay>
  );
}

SelectTemplatePopup.propTypes = {
  onClose: PropTypes.func.isRequired,
  onTemplateSelect: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  selectedTemplate: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
    output: PropTypes.string.isRequired,
    url: PropTypes.string,
    is_letterhead_enabled:PropTypes.bool,
  }),
  setSelectedTemplate: PropTypes.func.isRequired,
  file: PropTypes.shape({
    name: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
  }),
  setFile: PropTypes.func.isRequired,
  showUploadedFile: PropTypes.bool.isRequired,
  setShowUploadedFile: PropTypes.func.isRequired,
  handleError: PropTypes.func,
  selectedCategory: PropTypes.string,
  setSelectedCategory: PropTypes.func,
  selectedLetterhead: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    firm_name: PropTypes.string,
    firm_location: PropTypes.string,
    is_default: PropTypes.bool,
  }),
  setSelectedLetterhead: PropTypes.func,
};
